import { useCallback, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { Marke } from './Marke.js'
import { api } from '../net/apiClient.js'
import { notify } from '../ui/Toasts.js'
import { fehlerText, ApiError } from '../net/errors.js'
import { Stillgelegt, istStillgelegt } from './Stillgelegt.js'

/**
 * Anmelden – eigene Seite unter /login.
 *
 * Vorher war die Anmeldung die Startseite. Das hat zwei Dinge vermischt: die
 * Frage «was ist das hier» und die Frage «lass mich rein». Getrennt kann die
 * eine erklären und die andere schnell sein.
 *
 * Der zweite Schritt bleibt derselbe Bildschirm: das Passwortfeld weicht dem
 * Codefeld. Ein Seitenwechsel mittendrin erzeugt bei schlechter Verbindung
 * genau den Moment, in dem jemand neu lädt und wieder von vorn beginnt.
 *
 * Fehler stehen IM Formular und nicht nur als Meldung, die nach vier Sekunden
 * verschwindet. Wer sich vertippt hat, schaut auf das Feld – nicht in die Ecke,
 * in der eben etwas aufblitzte.
 *
 * ── Zur Zweiteilung ──────────────────────────────────────────────────────
 *
 * Links steht, wofür man sich anmeldet, rechts das Formular. Eine Maske
 * allein auf leerer Fläche sagt nichts darüber, wo man gerade ist – und genau
 * in diesem Moment fragt sich jemand, ob er sein Passwort auf der richtigen
 * Seite eintippt. Die linke Hälfte beantwortet das, bevor die Frage entsteht.
 *
 * Was hier bewusst NICHT steht: «weiter mit Google», «weiter mit GitHub».
 * Raporto hat keine Fremdanmeldung. Zwei Knöpfe, die nach einem Klick nichts
 * tun, wären ausgerechnet auf der Anmeldeseite die schlechteste Stelle für
 * eine Behauptung.
 *
 * Auch die linke Hälfte borgt sich nichts von der Startseite: kein Gerät,
 * kein Beleg von dort, sondern ein langsam kreisendes Licht. Jede öffentliche
 * Seite hat ihr eigenes Zeichen bei gleicher Palette und gleicher Schrift.
 *
 * Neu ist die Augentaste am Passwortfeld. Sie ist kein Zierrat: ein Betrieb
 * tippt sein Passwort auf einer Baustelle mit Handschuhen ein, und drei
 * Fehlversuche wegen eines vertippten Zeichens sind ärgerlicher als ein
 * kurzer Blick auf das, was man geschrieben hat. Standard bleibt verdeckt.
 */

function Auge({ offen }: { offen: boolean }): JSX.Element {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {offen && <path d="M3 3l18 18" />}
    </svg>
  )
}

export function Login({
  aufAngemeldet,
  aufStart
}: {
  aufAngemeldet: (s: SessionUser) => void
  aufStart: () => void
}): JSX.Element {
  const [email, setEmail] = useState('')
  const [passwort, setPasswort] = useState('')
  const [code, setCode] = useState('')
  const [zweiterFaktor, setZweiterFaktor] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  /*
    Eine Stilllegung ist kein Fehlertext, sondern eine eigene Seite.

    Der Zustand steht neben `fehler` und nicht darin: Ein 403 wegen
    Stilllegung bekommt eine Erklärung mit Daten und Handlungsmöglichkeit,
    jeder andere Fehler eine Zeile. Beides in ein Feld zu pressen hiesse, die
    Zeile aus der Seite zu bauen oder umgekehrt.
  */
  const [stillgelegt, setStillgelegt] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [sichtbar, setSichtbar] = useState(false)
  const [vergessenLaeuft, setVergessenLaeuft] = useState(false)
  const [vergessenHinweis, setVergessenHinweis] = useState<string | null>(null)

  /**
   * Einen Rücksetzlink anfordern.
   *
   * Die einzige Stelle, an der die Oberfläche etwas NICHT sagen darf: ob es
   * die Adresse gibt. Sie weiss es auch nicht – der Server antwortet in beiden
   * Fällen gleich, und dieser Bildschirm gibt genau das weiter.
   */
  const passwortVergessen = useCallback(async () => {
    if (!email.trim()) {
      setVergessenHinweis('Bitte zuerst die E-Mail-Adresse eintragen.')
      return
    }
    setVergessenLaeuft(true)
    setFehler(null)
    try {
      const a = await api.passwortVergessen(email.trim())
      setVergessenHinweis(
        a.zustellungEcht
          ? a.hinweis
          : `${a.hinweis} Hinweis für den Feldtest: der Versand läuft noch über den ` +
            'Entwicklungs-Ausgang, die Nachricht wird dort abgelegt statt zugestellt.'
      )
    } catch (e) {
      setVergessenHinweis(fehlerText(e))
    } finally {
      setVergessenLaeuft(false)
    }
  }, [email])

  const anmelden = useCallback(async () => {
    setFehler(null)
    setBusy(true)
    try {
      const r = await api.login(email.trim(), passwort)
      if (r.zweiterFaktorNoetig) {
        // Das Passwort verlässt den Speicher, sobald es nicht mehr gebraucht wird.
        setPasswort('')
        setSichtbar(false)
        setZweiterFaktor(true)
        return
      }
      aufAngemeldet(r.session as SessionUser)
    } catch (err) {
      if (istStillgelegt(err)) setStillgelegt(err)
      else setFehler(fehlerText(err))
    } finally {
      setBusy(false)
    }
  }, [aufAngemeldet, email, passwort])

  const codePruefen = useCallback(async () => {
    setFehler(null)
    setBusy(true)
    try {
      const antwort = await api.zweiterFaktor(code.trim())
      if (antwort.hinweis) notify.info(antwort.hinweis)
      const me = await api.me()
      aufAngemeldet(me.session as SessionUser)
    } catch (err) {
      if (istStillgelegt(err)) setStillgelegt(err)
      else setFehler(fehlerText(err))
    } finally {
      setBusy(false)
    }
  }, [aufAngemeldet, code])

  /*
    Die Abzweigung. Steht vor dem Anmeldeformular, weil es nach einer
    Stilllegung nichts mehr zu tippen gibt: Dieselbe Adresse mit demselben
    Passwort führt wieder hierher.
  */
  if (stillgelegt !== null) {
    return (
      <Stillgelegt
        fehler={stillgelegt}
        aufZurueck={() => {
          setStillgelegt(null)
          setPasswort('')
          setCode('')
          setZweiterFaktor(false)
        }}
      />
    )
  }

  return (
    <div className="rp-pg rp-pg-anmeldung">
      <div className="rp-an">
        <aside className="rp-an-marke">
          <div className="rp-an-knopfzeile">
            <button className="rp-pg-marke" type="button" onClick={aufStart} id="btn-marke">
              <Marke groesse={30} />
            </button>
            <button
              className="rp-pg-knopf rp-pg-knopf-glas rp-pg-knopf-klein"
              type="button"
              id="btn-zurueck-start"
              onClick={aufStart}
            >
              Zurück zur Übersicht
            </button>
          </div>

          <p className="rp-an-satz">Die Betriebszentrale wartet schon.</p>

          {/*
            Drei Punkte über das Produkt, keine Zusagen über Betrieb und Daten.

            Hier standen «Datenbank in Zürich, je Betrieb strikt getrennt» und
            «Export als Datei, ohne Nachfrage und ohne Frist». Beides ging
            weiter als das, was die Rechtstexte sagen – der Ort der Daten ist
            dort als Schweiz und/oder EU beschrieben, und der Export hat einen
            benannten Umfang. Eine Anmeldeseite ist der falsche Ort, um von
            beidem eine schärfere Fassung zu behaupten: wer es später in der
            Datenschutzerklärung anders liest, glaubt keiner von beiden.

            «Für Superuser» beim zweiten Faktor ist der Stand im Code:
            RAPORTO_REQUIRE_2FA greift für diese Rolle, nicht für jede.
          */}
          <ul className="rp-an-punkte">
            <li>Nutzung im Browser und am Handy</li>
            <li>Zwei-Faktor-Schutz für Superuser</li>
            <li>Offerten, Rapporte und Rechnungen in einem Zugang</li>
          </ul>
        </aside>

        <main className="rp-an-seite">
          <form
            className="rp-an-karte"
            onSubmit={(e) => {
              e.preventDefault()
              void (zweiterFaktor ? codePruefen() : anmelden())
            }}
          >
            <span className="rp-strahl" aria-hidden="true">
              <span />
            </span>
            {zweiterFaktor ? (
              <>
                <h1>Code eingeben</h1>
                <p className="rp-an-unter">
                  Der sechsstellige Code aus deiner Authenticator-App. Ein Wiederherstellungscode
                  geht auch.
                </p>

                {fehler && (
                  <p className="rp-an-fehler" id="login-fehler">
                    {fehler}
                  </p>
                )}

                <div className="rp-an-feld rp-an-code">
                  <label htmlFor="code">Code</label>
                  <div className="rp-an-eingabe">
                    <input
                      id="code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      autoFocus
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>
                </div>

                <button
                  className="rp-pg-knopf rp-pg-knopf-hell"
                  id="btn-2fa-login"
                  type="submit"
                  disabled={busy || code.trim().length < 6}
                >
                  {busy ? 'Prüft …' : 'Weiter'}
                </button>

                <p className="rp-an-hinweis">
                  <button
                    type="button"
                    className="rp-an-linklike"
                    id="btn-2fa-zurueck"
                    onClick={() => {
                      setZweiterFaktor(false)
                      setCode('')
                      setFehler(null)
                    }}
                  >
                    Zurück zur Anmeldung
                  </button>
                </p>
              </>
            ) : (
              <>
                <h1>Anmelden</h1>
                <p className="rp-an-unter">Mit deinem Betriebszugang.</p>

                {fehler && (
                  <p className="rp-an-fehler" id="login-fehler">
                    {fehler}
                  </p>
                )}

                <div className="rp-an-feld">
                  <label htmlFor="email">E-Mail</label>
                  <div className="rp-an-eingabe">
                    <input
                      id="email"
                      type="email"
                      autoComplete="username"
                      inputMode="email"
                      autoFocus
                      placeholder="name@betrieb.ch"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                <div className="rp-an-feld">
                  <label htmlFor="password">Passwort</label>
                  <div className="rp-an-eingabe">
                    <input
                      id="password"
                      type={sichtbar ? 'text' : 'password'}
                      data-geheim=""
                      autoComplete="current-password"
                      value={passwort}
                      onChange={(e) => setPasswort(e.target.value)}
                    />
                    <button
                      type="button"
                      className="rp-an-auge"
                      id="btn-passwort-sichtbar"
                      aria-label={sichtbar ? 'Passwort verbergen' : 'Passwort anzeigen'}
                      aria-pressed={sichtbar}
                      onClick={() => setSichtbar((v) => !v)}
                    >
                      <Auge offen={sichtbar} />
                    </button>
                  </div>
                </div>

                <button
                  className="rp-pg-knopf rp-pg-knopf-hell"
                  id="btn-login"
                  type="submit"
                  disabled={busy || !email.trim() || passwort.length === 0}
                >
                  {busy ? 'Melde an …' : 'Anmelden'}
                </button>

                {/*
                  «Passwort vergessen» – der häufigste Weg in jede Software.

                  Hier stand bis zuletzt, der Betrieb setze es zurück und einen
                  Weg per Mail gebe es aus Sicherheitsgründen nicht. Beides war
                  falsch: der Betrieb konnte es nicht, und sicherer war daran
                  nichts – der Weg endete beim Anbieter und einem Eingriff von
                  Hand in die Datenbank.

                  Die Antwort ist immer dieselbe, auch wenn es die Adresse nicht
                  gibt. Sonst wäre dieses Feld ein Adressverzeichnis.
                */}
                <p className="rp-an-hinweis">
                  <button
                    type="button"
                    className="rp-an-linklike"
                    id="btn-passwort-vergessen"
                    disabled={vergessenLaeuft}
                    onClick={() => void passwortVergessen()}
                  >
                    Passwort vergessen?
                  </button>
                </p>

                {vergessenHinweis && (
                  <p className="rp-an-hinweis" id="vergessen-hinweis">
                    {vergessenHinweis}
                  </p>
                )}
              </>
            )}
          </form>
        </main>
      </div>
    </div>
  )
}
